import { useEffect, useState } from "react";
import { sampleArticles } from "@/lib/sample-articles";
import { ArticleCard } from "@/components/ArticleCard";
import { SEO } from "@/components/SEO";
import type { Article } from "@/types";

export function HomePage() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchArticles() {
      try {
        // Dynamic import — Firebase chunk loads AFTER initial paint, not during
        const { getArticles } = await import("@/lib/firebase");
        const data = await getArticles();
        setArticles(data.length > 0 ? data : sampleArticles);
      } catch (err) {
        console.error("Erreur chargement articles:", err);
        setArticles(sampleArticles);
      } finally {
        setLoading(false);
      }
    }
    fetchArticles();
  }, []);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-5 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="animate-pulse space-y-4">
              <div className="aspect-[16/10] bg-muted rounded-2xl w-full" />
              <div className="h-4 bg-muted rounded w-1/3" />
              <div className="h-6 bg-muted rounded w-3/4" />
              <div className="h-4 bg-muted rounded w-full" />
              <div className="h-4 bg-muted rounded w-2/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const blogPostingItems = articles.slice(0, 10).map((article) => ({
    "@type": "BlogPosting",
    headline: article.title,
    description: article.excerpt,
    url: `https://le-cobaye.fr/article/${article.slug}`,
    ...(article.coverImage ? { image: article.coverImage } : {}),
    datePublished: article.createdAt.toISOString(),
    dateModified: article.updatedAt.toISOString(),
    author: {
      "@type": "Person",
      name: article.author,
    },
  }));

  return (
    <main className="max-w-6xl mx-auto px-5 pb-20">
      <SEO
        path="/"
        description="Le Cobaye teste les formations en ligne pour vous. Avis honnetes, comparatifs et recommandations sur les meilleurs cours en ligne."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Le Cobaye",
          url: "https://le-cobaye.fr",
          description:
            "Le Cobaye teste les formations en ligne pour vous donner des avis honnetes et detailles.",
          inLanguage: "fr",
          publisher: {
            "@type": "Organization",
            name: "Le Cobaye",
            url: "https://le-cobaye.fr",
          },
          blogPost: blogPostingItems,
        }}
        breadcrumbs={[{ name: "Accueil", path: "/" }]}
      />

      <section className="py-20 md:py-32 max-w-3xl mx-auto text-center">
        <span className="inline-block py-1 px-3 rounded-full bg-primary/10 text-primary text-xs font-semibold tracking-wider uppercase mb-6">
          Votre cobaye independant
        </span>
        <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-foreground mb-6 font-heading">
          Tests et avis honnetes sur les <span className="text-primary/70">formations en ligne</span>.
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
          Le Cobaye teste les formations en ligne du marche pour que vous n'ayez pas a le faire. Avis honnetes, comparatifs detailles et recommandations sur les meilleurs cours en ligne — sans langue de bois.
        </p>
      </section>

      {articles.length === 0 ? (
        <div className="py-20 text-center bg-muted/30 rounded-3xl border border-dashed border-border">
          <p className="text-lg text-muted-foreground">
            Aucun article pour le moment. Revenez bientot !
          </p>
        </div>
      ) : (
        <section>
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-2xl font-bold tracking-tight">Derniers articles</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {articles.map((article, index) => (
              <ArticleCard key={article.id} article={article} priority={index === 0} />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
